const SIMPLE_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidDate(date) {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

function parseDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === 'string') {
    const simpleDate = value.match(SIMPLE_DATE_REGEX);

    if (simpleDate) {
      const year = Number(simpleDate[1]);
      const month = Number(simpleDate[2]);
      const day = Number(simpleDate[3]);
      const date = new Date(year, month - 1, day);

      if (!isValidDate(date)) {
        return null;
      }

      if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
        return date;
      }

      return null;
    }
  }

  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
}

export function getIssuedAccountingOrders(orders = []) {
  if (!Array.isArray(orders)) {
    return [];
  }

  return orders.filter(
    order =>
      order?.status?.order === 'invoiced' &&
      order?.status?.payment === 'unpaid' &&
      order?.typeOf !== 'creditNote'
  );
}

/**
 * Returns the first invoiced, unpaid, non-credit-note order from
 * `getIssuedAccountingOrders(orders)`.
 * This assumes callers pass the API order list in descending chronological order,
 * where drafts/paid/canceled/credit-note entries are already intermixed and
 * non-active entries will be filtered out.
 * If API sorting changes, callers/tests should be updated accordingly.
 */
export function getLatestIssuedAccountingOrder(orders = []) {
  return getIssuedAccountingOrders(orders)[0] || null;
}

export function getOrderReferenceDate(order) {
  if (!order) {
    return null;
  }

  const candidates = [order?.date?.due, order?.date?.invoice];

  for (const candidate of candidates) {
    const parsed = parseDateValue(candidate);

    if (parsed) {
      return parsed;
    }
  }

  return null;
}

export function getEndOfMonth(dateValue) {
  const date = parseDateValue(dateValue);

  if (!date) {
    return null;
  }

  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function formatDateAsISO(dateValue) {
  const date = parseDateValue(dateValue);

  if (!date) {
    return '';
  }

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function isEndOfMonthDate(dateValue) {
  const date = parseDateValue(dateValue);

  if (!date) {
    return false;
  }

  const endOfMonth = getEndOfMonth(date);
  return endOfMonth.getDate() === date.getDate();
}

export function isDateInPast(dateValue, now = new Date()) {
  const date = parseDateValue(dateValue);

  if (!date) {
    return false;
  }

  const today = parseDateValue(now);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return date.getTime() < todayStart.getTime();
}

export function isValidPendingChangeDate(dateValue, now = new Date()) {
  const date = parseDateValue(dateValue);

  if (!date) {
    return false;
  }

  return isEndOfMonthDate(date) && !isDateInPast(date, now);
}

export function getScheduledDowngradeDateFromOrders(
  orders = [],
  { fallbackToCurrentMonthEnd = false, now = new Date() } = {}
) {
  const latestIssuedOrder = getLatestIssuedAccountingOrder(orders);
  const referenceDate = getOrderReferenceDate(latestIssuedOrder);
  const currentMonthEnd = getEndOfMonth(now);

  if (referenceDate) {
    const referenceMonthEnd = getEndOfMonth(referenceDate);

    if (referenceMonthEnd && !isDateInPast(referenceMonthEnd, now)) {
      return referenceMonthEnd;
    }

    return currentMonthEnd;
  }

  if (fallbackToCurrentMonthEnd) {
    return currentMonthEnd;
  }

  return null;
}

export function getScheduledDowngradeDateISO(
  orders = [],
  { fallbackToCurrentMonthEnd = false, now = new Date() } = {}
) {
  const scheduledDate = getScheduledDowngradeDateFromOrders(orders, {
    fallbackToCurrentMonthEnd,
    now,
  });

  // `getScheduledDowngradeDateFromOrders` should already return a non-past end-of-month date,
  // but `getScheduledDowngradeDateISO` keeps `isValidPendingChangeDate` as a defensive guard
  // before `formatDateAsISO` to protect against future regressions in date-calculation logic.
  if (!scheduledDate || !isValidPendingChangeDate(scheduledDate, now)) {
    return null;
  }

  return formatDateAsISO(scheduledDate);
}
